import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Input,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { RefreshCw, Eye } from 'lucide-react';
import { avmApi } from '../../services/api';

const { Text } = Typography;

interface AvmCalculation {
  id: string;
  zoneId: string;
  zoneNeighborhood?: string;
  zoneMunicipality?: string;
  modo: 'inicial' | 'profesional';
  motor: 1 | 2;
  m2: number;
  isVillaOrPremium: boolean;
  precioM2Base: number;
  precioM2Barrio: number;
  valorBase: number;
  coefInmueble: number;
  coefZonaTotal: number;
  precioFinal: number;
  coefMicrozona: number;
  coefDemanda: number;
  coefConfianzaDispersion: number;
  coefHistorico: number;
  inputJson: any;
  coefInmuebleJson: Record<string, number>;
  resultadoJson: any;
  alertasJson: string[];
  source?: string;
  userEmail?: string;
  userPhone?: string;
  createdAt: string;
}

const fmtEur = (v?: number) =>
  typeof v === 'number'
    ? v.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
    : '-';
const fmtPct = (v?: number) =>
  typeof v === 'number' ? `${(v * 100).toFixed(2)}%` : '-';
const fmtNum = (v?: number) =>
  typeof v === 'number' ? v.toLocaleString('es-ES', { maximumFractionDigits: 2 }) : '-';

export default function AvmCalculationsTab() {
  const [rows, setRows] = useState<AvmCalculation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modo, setModo] = useState<string | undefined>(undefined);
  const [motor, setMotor] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selected, setSelected] = useState<AvmCalculation | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await avmApi.listCalculations({
        page,
        limit: pageSize,
        search: search || undefined,
        modo,
        motor,
      });
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch {
      message.error('No se pudo cargar el histórico de cálculos');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, modo, motor]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const columns = useMemo(
    () => [
      {
        title: 'Fecha',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 150,
        render: (v: string) => new Date(v).toLocaleString('es-ES'),
      },
      {
        title: 'Zona',
        key: 'zone',
        width: 220,
        render: (_: any, r: AvmCalculation) => (
          <div>
            <Text strong>{r.zoneId}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.zoneNeighborhood} {r.zoneMunicipality ? `· ${r.zoneMunicipality}` : ''}
            </Text>
          </div>
        ),
      },
      {
        title: 'Motor',
        dataIndex: 'motor',
        key: 'motor',
        width: 110,
        render: (v: number) =>
          v === 1 ? (
            <Tag color="green">Motor 1 (ventas reales)</Tag>
          ) : (
            <Tag color="orange">Motor 2 (×0.92)</Tag>
          ),
      },
      {
        title: 'Modo',
        dataIndex: 'modo',
        key: 'modo',
        width: 110,
        render: (v: string) =>
          v === 'profesional' ? <Tag color="blue">Profesional</Tag> : <Tag>Inicial</Tag>,
      },
      { title: 'm²', dataIndex: 'm2', key: 'm2', width: 70 },
      {
        title: 'Precio €/m² base',
        dataIndex: 'precioM2Base',
        key: 'precioM2Base',
        width: 130,
        render: (v: number) => fmtEur(v),
      },
      {
        title: 'Precio €/m² zona',
        dataIndex: 'precioM2Barrio',
        key: 'precioM2Barrio',
        width: 130,
        render: (v: number) => fmtEur(v),
      },
      {
        title: 'Coef inmueble',
        dataIndex: 'coefInmueble',
        key: 'coefInmueble',
        width: 110,
        render: (v: number) => fmtPct(v),
      },
      {
        title: 'Precio final',
        dataIndex: 'precioFinal',
        key: 'precioFinal',
        width: 140,
        render: (v: number) => <Text strong>{fmtEur(v)}</Text>,
      },
      {
        title: 'Alertas',
        dataIndex: 'alertasJson',
        key: 'alertasJson',
        width: 90,
        render: (v: string[]) =>
          v && v.length > 0 ? <Tag color="red">{v.length}</Tag> : <Tag>-</Tag>,
      },
      {
        title: 'Origen',
        dataIndex: 'source',
        key: 'source',
        width: 90,
        render: (v: string) => <Tag>{v || 'admin'}</Tag>,
      },
      {
        title: '',
        key: 'actions',
        width: 80,
        fixed: 'right' as const,
        render: (_: any, r: AvmCalculation) => (
          <Button
            size="small"
            icon={<Eye size={14} />}
            onClick={() => setSelected(r)}
          >
            Ver
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Buscar por zoneId, barrio, municipio o email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={() => {
              setPage(1);
              fetchRows();
            }}
            style={{ width: 360 }}
          />
          <Select
            allowClear
            placeholder="Modo"
            value={modo}
            onChange={(v) => {
              setModo(v);
              setPage(1);
            }}
            style={{ width: 140 }}
            options={[
              { value: 'inicial', label: 'Inicial' },
              { value: 'profesional', label: 'Profesional' },
            ]}
          />
          <Select
            allowClear
            placeholder="Motor"
            value={motor}
            onChange={(v) => {
              setMotor(v);
              setPage(1);
            }}
            style={{ width: 140 }}
            options={[
              { value: 1, label: 'Motor 1' },
              { value: 2, label: 'Motor 2' },
            ]}
          />
          <Button icon={<RefreshCw size={14} />} onClick={fetchRows}>
            Recargar
          </Button>
          <Text type="secondary">{total} cálculos</Text>
        </Space>
      </Card>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns as any}
        dataSource={rows}
        scroll={{ x: 1500 }}
        size="small"
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />

      <Drawer
        title={
          selected ? `Cálculo · ${selected.zoneId} · ${new Date(selected.createdAt).toLocaleString('es-ES')}` : ''
        }
        width={780}
        open={!!selected}
        onClose={() => setSelected(null)}
      >
        {selected && <CalculationDetail calc={selected} />}
      </Drawer>
    </>
  );
}

function CalculationDetail({ calc }: { calc: AvmCalculation }) {
  const motorLabel =
    calc.motor === 1
      ? 'Motor 1 — (media portales + media ventas reales) / 2'
      : 'Motor 2 — media portales × 0.92 (sin ventas reales fiables)';

  const coefInmuebleEntries = Object.entries(calc.coefInmuebleJson || {});

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert message={motorLabel} type={calc.motor === 1 ? 'success' : 'warning'} showIcon />

      {calc.alertasJson && calc.alertasJson.length > 0 && (
        <Alert
          message="Alertas del motor"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {calc.alertasJson.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          }
          type="warning"
          showIcon
        />
      )}

      <Card size="small" title="Resumen">
        <Space wrap size="large">
          <Statistic title="Precio final" value={calc.precioFinal} suffix="€" />
          <Statistic title="m²" value={calc.m2} />
          <Statistic title="€/m² zona" value={calc.precioM2Barrio} suffix="€" />
        </Space>
      </Card>

      <Card size="small" title="1) Precio €/m² base (motor)">
        <Descriptions column={1} size="small">
          <Descriptions.Item label="€/m² base resultante">
            {fmtEur(calc.precioM2Base)}
          </Descriptions.Item>
          <Descriptions.Item label="Fórmula motor">
            {calc.motor === 1 ? '(portales + ventas_reales) / 2' : 'portales × 0.92'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="2) Coeficientes de zona (multiplicativo)">
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="Microzona">{fmtPct(calc.coefMicrozona)}</Descriptions.Item>
          <Descriptions.Item label="Demanda">{fmtPct(calc.coefDemanda)}</Descriptions.Item>
          <Descriptions.Item label="Confianza/dispersión">
            {fmtPct(calc.coefConfianzaDispersion)}
          </Descriptions.Item>
          <Descriptions.Item label="Histórico">{fmtPct(calc.coefHistorico)}</Descriptions.Item>
          <Descriptions.Item label="Σ coef zona" span={2}>
            <Text strong>{fmtPct(calc.coefZonaTotal)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="€/m² zona = base × (1 + Σ)" span={2}>
            <Text strong>{fmtEur(calc.precioM2Barrio)}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="3) Valor base">
        <Descriptions column={1} size="small">
          <Descriptions.Item label="€/m² zona × m²">
            {fmtEur(calc.precioM2Barrio)} × {calc.m2} = <Text strong>{fmtEur(calc.valorBase)}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="4) Coeficientes del inmueble">
        {coefInmuebleEntries.length === 0 ? (
          <Text type="secondary">Sin coeficientes aplicados</Text>
        ) : (
          <Descriptions column={2} size="small" bordered>
            {coefInmuebleEntries.map(([k, v]) => (
              <Descriptions.Item key={k} label={k}>
                {fmtPct(v as number)}
              </Descriptions.Item>
            ))}
            <Descriptions.Item label="Σ tras clamp" span={2}>
              <Text strong>{fmtPct(calc.coefInmueble)}</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                (cap ±{calc.isVillaOrPremium ? '20' : '15'}%)
              </Text>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Card size="small" title="5) Precio final">
        <Descriptions column={1} size="small">
          <Descriptions.Item label="valor base × (1 + coef inmueble)">
            {fmtEur(calc.valorBase)} × (1 + {fmtPct(calc.coefInmueble)}) ={' '}
            <Text strong>{fmtEur(calc.precioFinal)}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="6) Resultado al usuario">
        {calc.resultadoJson?.tipo === 'inicial' ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Valor">{fmtEur(calc.resultadoJson.valor)}</Descriptions.Item>
            <Descriptions.Item label="Mínimo">{fmtEur(calc.resultadoJson.min)}</Descriptions.Item>
            <Descriptions.Item label="Máximo">{fmtEur(calc.resultadoJson.max)}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Bajo (×0.95)">
              {fmtEur(calc.resultadoJson?.bajo)}
            </Descriptions.Item>
            <Descriptions.Item label="Óptimo">
              {fmtEur(calc.resultadoJson?.optimo)}
            </Descriptions.Item>
            <Descriptions.Item label="Alto (×1.10)">
              {fmtEur(calc.resultadoJson?.alto)}
            </Descriptions.Item>
            <Descriptions.Item label="Catastro aplicado">
              {calc.resultadoJson?.usoCatastro ? 'Sí' : 'No'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Card size="small" title="Inputs originales">
        <pre style={{ fontSize: 11, margin: 0, maxHeight: 260, overflow: 'auto' }}>
          {JSON.stringify(calc.inputJson, null, 2)}
        </pre>
      </Card>
    </Space>
  );
}
